export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, message, code, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
let baseUrl = 'http://localhost:3000/api/v1';
let getToken = () => null;
let setToken = () => { };
/** Called with the token that just failed to refresh. Wired by the caller to
 *  clear the session — but only if that token is still the current one, see
 *  the callers in each app's `configure-api.ts`. */
let onAuthFailure = () => { };
/** Where a request actually goes: the path to send, or `null` to refuse it
 *  without a round trip. Must map an already-routed path to itself — a retry
 *  after refresh routes again. Identity unless an app sets one. */
let routeRequest = (path) => path;
export function configureApi(opts) {
    if (opts.baseUrl)
        baseUrl = opts.baseUrl;
    if (opts.getToken)
        getToken = opts.getToken;
    if (opts.setToken)
        setToken = opts.setToken;
    if (opts.onAuthFailure)
        onAuthFailure = opts.onAuthFailure;
    if (opts.routeRequest)
        routeRequest = opts.routeRequest;
}
/**
 * The refresh cookie is single-use — the server deletes it and mints a new
 * one on every redemption. Two tabs (or two apps on the same origin) racing
 * to refresh at once means only one wins; the other gets a dead cookie and a
 * spurious 401. `refreshPromise` already dedupes overlapping calls *within
 * this tab*; `navigator.locks` extends that across every tab of this origin
 * by serializing refreshes instead of letting them race — the loser simply
 * waits its turn and redeems the (by-then-rotated) cookie for a fresh token.
 */
async function withRefreshLock(fn) {
    if (typeof navigator === 'undefined' || !('locks' in navigator))
        return fn();
    return navigator.locks.request('scc-auth-refresh', fn);
}
let refreshPromise = null;
async function attemptRefreshData() {
    if (refreshPromise)
        return refreshPromise;
    refreshPromise = withRefreshLock(async () => {
        try {
            const res = await fetch(`${baseUrl}/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: { Accept: 'application/json' },
            });
            if (!res.ok)
                return null;
            const payload = (await res.json());
            if (!payload.success || !payload.data?.token)
                return null;
            setToken(payload.data.token);
            return payload.data;
        }
        catch {
            return null;
        }
        finally {
            refreshPromise = null;
        }
    });
    return refreshPromise;
}
async function attemptRefresh() {
    const data = await attemptRefreshData();
    return data?.token ?? null;
}
/**
 * The single entry point for redeeming the shared refresh cookie. Every
 * caller — the automatic retry-on-401 below, and any app's explicit
 * "hydrate my session on load" call — must go through this one function so
 * they share the same in-tab dedupe and cross-tab lock. A second, separate
 * `fetch('/auth/refresh')` anywhere else in an app reopens the exact race
 * this file exists to close.
 */
export async function refreshSession() {
    return attemptRefreshData();
}
async function rawRequest(requested, opts) {
    const { method = 'GET', body, signal, skipAuthRefresh } = opts;
    const path = routeRequest(requested, { method, headers: opts.headers ?? {} });
    if (path === null)
        throw new ApiError(403, 'Not available here');
    const token = opts.token === undefined ? getToken() : opts.token;
    const headers = { Accept: 'application/json' };
    if (body !== undefined)
        headers['Content-Type'] = 'application/json';
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    if (opts.headers)
        Object.assign(headers, opts.headers);
    let res;
    try {
        res = await fetch(`${baseUrl}${path}`, {
            method,
            headers,
            credentials: 'include',
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal,
        });
    }
    catch (err) {
        throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
    }
    if (res.status === 401 && token && !skipAuthRefresh) {
        const newToken = await attemptRefresh();
        if (newToken) {
            return rawRequest(path, { ...opts, token: newToken, skipAuthRefresh: true });
        }
        onAuthFailure(token);
    }
    // A successful reply with nothing in it — 204 from a DELETE — is success,
    // not a parse failure. Treating it as one reported every plan deletion as
    // "Request failed" after the plan was already gone.
    if (res.ok && (res.status === 204 || res.headers.get('content-length') === '0')) {
        return { res, payload: { success: true, data: undefined } };
    }
    let payload = null;
    try {
        payload = (await res.json());
    }
    catch {
        throw new ApiError(res.status, res.statusText || 'Request failed');
    }
    if (!res.ok || !payload || payload.success === false) {
        const err = payload && payload.success === false ? payload : null;
        throw new ApiError(res.status, err?.error ?? res.statusText ?? 'Request failed', err?.code, err?.details);
    }
    return { res, payload };
}
export async function apiRequest(path, opts = {}) {
    const { payload } = await rawRequest(path, opts);
    if (payload && 'data' in payload && payload.data !== undefined) {
        return payload.data;
    }
    const { success: _success, ...rest } = payload ?? { success: true };
    return rest;
}
/**
 * Like `apiRequest`, but also surfaces sibling envelope fields under `meta`
 * (e.g. `{ success, data, meta: { dataThrough } }`) — for endpoints whose
 * response carries more than just `data`.
 */
export async function apiRequestEnvelope(path, opts = {}) {
    const { payload } = await rawRequest(path, opts);
    const data = (payload && 'data' in payload ? payload.data : undefined);
    const meta = (payload && 'meta' in payload ? payload.meta : undefined);
    return { data, meta };
}
//# sourceMappingURL=client.js.map